import type { EmployeePayrollResultV1 } from "@impetusos/contracts"
import type { ReactElement } from "react"

export interface EmployeePayrollResultPanelProps {
  status: "loading" | "ready" | "empty" | "error" | "denied"
  result?: EmployeePayrollResultV1
  employeeLabel?: string
  onRetry: () => void
  onDownload: (accessEventId: string) => void
}

const USD_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
})

const money = (amount: string): string => USD_FORMATTER.format(Number(amount))

export function EmployeePayrollResultPanel({
  status,
  result,
  employeeLabel = "Employee",
  onRetry,
  onDownload,
}: EmployeePayrollResultPanelProps): ReactElement {
  if (status === "loading") {
    return <p aria-live="polite">Loading payroll result…</p>
  }
  if (status === "denied") {
    return <p role="alert">You do not have access to this payroll result.</p>
  }
  if (status === "error") {
    return (
      <div role="alert">
        <p>Payroll results are temporarily unavailable.</p>
        <button type="button" onClick={onRetry}>Try again</button>
      </div>
    )
  }
  if (status === "empty" || !result) {
    return <p>No finalized employee payroll result is available.</p>
  }

  const settlementLabel = {
    processing: "Processing",
    settled: "Settled",
    failed: "Failed",
  }[result.state]

  return (
    <section aria-labelledby="employee-payroll-result-title">
      <header>
        <p>Payroll · Powered by Worklio</p>
        <h2 id="employee-payroll-result-title">{employeeLabel} payroll result</h2>
        <p>
          {result.payPeriodStart} through {result.payPeriodEnd}
        </p>
      </header>
      <dl>
        <div><dt>Gross pay</dt><dd>{money(result.grossAmount)}</dd></div>
        <div><dt>Taxes</dt><dd>{money(result.taxAmount)}</dd></div>
        <div><dt>Deductions</dt><dd>{money(result.deductionAmount)}</dd></div>
        <div><dt>Net pay</dt><dd>{money(result.netAmount)}</dd></div>
        <div><dt>Settlement</dt><dd>{settlementLabel}</dd></div>
      </dl>
      <p>
        Amounts and settlement status are supplied by Worklio and are not
        recalculated by ImpetusOS.
      </p>
      {result.documentStatus === "available" ? (
        <button
          type="button"
          onClick={() => onDownload(result.accessEventId)}
          aria-label={`Download salary slip for ${employeeLabel}`}
        >
          Download salary slip
        </button>
      ) : (
        <p role="status">Salary slip not yet available.</p>
      )}
    </section>
  )
}
